/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'

import PasswordRequirements from './password-requirements'
import useRegistrationFields from './useRegistrationFields'
import FieldNewDesign from '../field-new-design'
import {colors} from '../../constants'

const RegistrationFields = ({form, prefix = '', isStoreRegistration}) => {
    const fields = useRegistrationFields({form, prefix})

    const password = form.watch('password')
    const {formatMessage} = useIntl()

    const messages = useMemo(() => {
        return {
            'use_registration_fields.error.required_first_name': formatMessage({
                defaultMessage: 'First name is required',
                id: 'use_registration_fields.error.required_first_name'
            }),
            'use_registration_fields.error.required_last_name': formatMessage({
                defaultMessage: 'Last name is required',
                id: 'use_registration_fields.error.required_last_name'
            }),
            'use_registration_fields.error.required_email': formatMessage({
                defaultMessage: 'Email is required',
                id: 'use_registration_fields.error.required_email'
            }),
            'use_registration_fields.label.mobile_number': formatMessage({
                defaultMessage: 'Phone is required',
                id: 'use_registration_fields.label.mobile_number'
            }),
            'use_registration_fields.error.required_password': formatMessage({
                defaultMessage: 'Password is required',
                id: 'use_registration_fields.error.required_password'
            }),
            'use_registration_fields.error.required_new_password':
                formatMessage({
                    defaultMessage: 'New password is required',
                    id: 'use_registration_fields.error.required_new_password'
                }),
            'use_registration_fields.error.minimum_characters': formatMessage({
                defaultMessage: 'Password must contain at least 8 characters',
                id: 'use_registration_fields.error.minimum_characters'
            }),
            'use_registration_fields.error.uppercase_letter': formatMessage({
                defaultMessage:
                    'Password must contain at least one uppercase letter',
                id: 'use_registration_fields.error.uppercase_letter'
            }),
            'use_registration_fields.error.lowercase_letter': formatMessage({
                defaultMessage:
                    'Password must contain at least one lowercase letter',
                id: 'use_registration_fields.error.lowercase_letter'
            }),
            'use_registration_fields.error.contain_number': formatMessage({
                defaultMessage: 'Password must contain at least one number',
                id: 'use_registration_fields.error.contain_number'
            }),
            'use_registration_fields.error.special_character': formatMessage({
                defaultMessage:
                    'Password must contain at least one special character',
                id: 'use_registration_fields.error.special_character'
            }),
            'use_registration_fields.error.has_no_space': formatMessage({
                defaultMessage: 'Not Include spaces.',
                id: 'use_registration_fields.error.special_character'
            }),
            'use_address_fields.error.please_select_your_state_or_province':
                formatMessage({
                    defaultMessage: 'Please select your state or province',
                    id: 'use_address_fields.error.please_select_your_state_or_province'
                }),
            'use_address_fields.error.required_language': formatMessage({
                defaultMessage: 'Please select a language',
                id: 'use_address_fields.error.required_language'
            }),
            'use_registration_fields.error.valid_date_of_birth': formatMessage({
                defaultMessage: 'Please select a valid Date of birth',
                id: 'use_address_fields.error.please_select_a_valid_date'
            })
        }
    }, [])

    const inputStyling = {
        width: '100%',
        borderColor: '#E6E6E6',
        paddingLeft: '26px',
        paddingY: '15px',
        borderRadius: '33px',
        height: 'fit-content',
        fontWeight: '300',
        color: colors.black,
        fontSize: '14px',
        fontFamily: 'Roboto',
        _placeholder: {
            color: colors.black,
            fontSize: '14px',
            fontFamily: 'Roboto'
        }
    }
    return (
        <Stack spacing="33px">
            <FieldNewDesign
                {...fields.firstName}
                inputProps={inputStyling}
                messages={messages}
            />
            <FieldNewDesign
                {...fields.lastName}
                inputProps={inputStyling}
                messages={messages}
            />
            <FieldNewDesign
                {...fields.email}
                inputProps={inputStyling}
                messages={messages}
            />
            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.emailConsent}
                    inputProps={{
                        alignItems: 'flex-start',
                        fontWeight: '300',
                        color: colors.black,
                        fontSize: '14px',
                        fontFamily: 'Roboto'
                    }}
                    messages={messages}
                    radioStyles={{
                        color: colors.black,
                        fontSize: '14px',
                        fontFamily: 'Roboto',
                        fontWeight: '300'
                    }}
                />
            </Box>

            {!isStoreRegistration && (
                <Stack spacing="13px">
                    <FieldNewDesign
                        {...fields.password}
                        inputProps={inputStyling}
                        messages={messages}
                        labelProps={{
                            fontWeight: '20',
                            fontSize: '15px'
                        }}
                    />
                    <PasswordRequirements value={password} />
                </Stack>
            )}
            <FieldNewDesign
                {...fields.mobileNumber}
                inputProps={({onChange}) => ({
                    ...inputStyling,
                    ...fields.mobileNumber.inputProps({onChange})
                })}
                messages={messages}
            />

            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.messagesConsent}
                    inputProps={{alignItems: 'flex-start', fontWeight: '300'}}
                    messages={messages}
                    radioStyles={{
                        color: colors.black,
                        fontSize: '14px',
                        fontFamily: 'Roboto',
                        fontWeight: '300'
                    }}
                />
            </Box>
            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.phoneConsent}
                    inputProps={{alignItems: 'flex-start', fontWeight: '300'}}
                    messages={messages}
                    radioStyles={{
                        color: colors.black,
                        fontSize: '14px',
                        fontFamily: 'Roboto',
                        fontWeight: '300'
                    }}
                />
            </Box>

            <FieldNewDesign
                {...fields.dateOfBirth}
                inputProps={{
                    ...inputStyling,
                    placeholder: formatMessage({
                        defaultMessage: 'Date of Birth (optional)',
                        id: 'use_registration_fields.label.date_of_birth'
                    })
                }}
                placeholder={formatMessage({
                    defaultMessage: 'Date of Birth (optional)',
                    id: 'use_registration_fields.label.date_of_birth'
                })}
                messages={messages}
            />

            <FieldNewDesign
                {...fields.province}
                inputProps={{
                    ...inputStyling
                }}
                messages={messages}
            />

            <FieldNewDesign
                {...fields.language}
                inputProps={{
                    ...inputStyling
                }}
                messages={messages}
            />

            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.acceptsMarketing}
                    inputProps={{alignItems: 'flex-start', fontWeight: '300'}}
                    messages={messages}
                />
            </Box>
            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.agreeToUseOfInformation}
                    inputProps={{alignItems: 'flex-start', fontWeight: '300'}}
                    messages={messages}
                />
            </Box>
            <Box marginLeft="10px">
                <FieldNewDesign
                    {...fields.agreeToTerms}
                    inputProps={{alignItems: 'flex-start', fontWeight: '300'}}
                    messages={messages}
                />
            </Box>
        </Stack>
    )
}

RegistrationFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,
    isStoreRegistration: PropTypes.bool
}

export default RegistrationFields
